/**
 * InventoryItemDetail
 *
 * Per-item drill-down: catalog metadata, per-location stock, and the full
 * transaction history filtered to this item. Reachable via /inventory/items/:id.
 */

import {
	type InventoryItem,
	type InventoryStockRow,
	type InventoryTransactionRow,
	type InventoryTransactionType,
	getInventoryItem,
	listInventoryStock,
	listInventoryTransactions,
} from "@/services/inventory-service";
import {
	ArrowLeftOutlined,
	BellOutlined,
	BoxPlotOutlined,
	HistoryOutlined,
	WarningOutlined,
} from "@ant-design/icons";
import {
	Button,
	Descriptions,
	Empty,
	Space,
	Spin,
	Table,
	Tag,
	message,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import "./Inventory.scss";

const TX_COLORS: Record<InventoryTransactionType, string> = {
	RECEIVE: "green",
	USE: "blue",
	ADJUST: "purple",
	TRANSFER_IN: "cyan",
	TRANSFER_OUT: "orange",
};

const formatDateTime = (d: string | null | undefined) =>
	d
		? new Date(d).toLocaleString(undefined, {
				month: "short",
				day: "numeric",
				year: "2-digit",
				hour: "numeric",
				minute: "2-digit",
			})
		: "—";

const InventoryItemDetail = () => {
	const { id } = useParams<{ id: string }>();
	const navigate = useNavigate();

	const [item, setItem] = useState<InventoryItem | null>(null);
	const [stock, setStock] = useState<InventoryStockRow[]>([]);
	const [transactions, setTransactions] = useState<InventoryTransactionRow[]>(
		[],
	);
	const [loading, setLoading] = useState(true);

	const itemId = id ? Number(id) : 0;

	const fetchAll = useCallback(async () => {
		if (!itemId) return;
		setLoading(true);
		try {
			const [itemResult, stockResult, txResult] = await Promise.all([
				getInventoryItem(itemId),
				listInventoryStock({ itemId, limit: 200 }),
				listInventoryTransactions({ itemId, limit: 100 }),
			]);
			setItem(itemResult);
			setStock(stockResult.stock);
			setTransactions(txResult.transactions);
		} catch (e: any) {
			console.error(e);
			message.error(e.message ?? "Failed to load item");
		} finally {
			setLoading(false);
		}
	}, [itemId]);

	useEffect(() => {
		fetchAll();
	}, [fetchAll]);

	if (loading) {
		return (
			<div style={{ padding: 40, display: "flex", justifyContent: "center" }}>
				<Spin />
			</div>
		);
	}

	if (!item) {
		return (
			<div style={{ padding: 24 }}>
				<Button
					icon={<ArrowLeftOutlined />}
					onClick={() => navigate("/inventory")}
					style={{ marginBottom: 16 }}
				>
					Back
				</Button>
				<Empty description="Item not found" />
			</div>
		);
	}

	const stockColumns: ColumnsType<InventoryStockRow> = [
		{ title: "Location", dataIndex: "locationName" },
		{
			title: "On Hand",
			dataIndex: "quantityOnHand",
			align: "right",
			render: (q: number) => (
				<span style={{ fontFamily: "monospace", fontWeight: 600 }}>
					{q} {item.unit}
				</span>
			),
		},
		{
			title: "Threshold",
			dataIndex: "effectiveThreshold",
			align: "right",
			render: (v) => v ?? "—",
		},
		{
			title: "Status",
			key: "status",
			render: (_, row) =>
				row.isLowStock ? (
					<Tag color="orange" icon={<WarningOutlined />}>
						LOW
					</Tag>
				) : (
					<Tag color="green">OK</Tag>
				),
		},
	];

	const txColumns: ColumnsType<InventoryTransactionRow> = [
		{
			title: "When",
			dataIndex: "createdAt",
			width: 160,
			render: formatDateTime,
		},
		{
			title: "Type",
			dataIndex: "transactionType",
			width: 130,
			render: (t: InventoryTransactionType) => (
				<Tag color={TX_COLORS[t]}>{t.replace("_", " ")}</Tag>
			),
		},
		{ title: "Location", dataIndex: "locationName" },
		{
			title: "Qty",
			dataIndex: "quantityDelta",
			align: "right",
			width: 100,
			render: (n: number) => (
				<span
					style={{
						color: n > 0 ? "#52c41a" : n < 0 ? "#ff4d4f" : undefined,
						fontFamily: "monospace",
						fontWeight: 600,
					}}
				>
					{n > 0 ? "+" : ""}
					{n}
				</span>
			),
		},
		{ title: "After", dataIndex: "quantityAfter", align: "right", width: 80 },
		{ title: "Asset", dataIndex: "assetTag", render: (v) => v ?? "—" },
		{ title: "By", dataIndex: "performedByUserName", render: (v) => v ?? "—" },
		{
			title: "Notes",
			dataIndex: "notes",
			ellipsis: true,
			render: (v) => v ?? "",
		},
	];

	const totalOnHand = stock.reduce((sum, s) => sum + s.quantityOnHand, 0);
	const lowCount = stock.filter((s) => s.isLowStock).length;

	return (
		<div className="inventory-page" style={{ padding: 16 }}>
			<Button
				icon={<ArrowLeftOutlined />}
				onClick={() => navigate("/inventory")}
				style={{ marginBottom: 16 }}
			>
				Back to Inventory
			</Button>

			<h2 style={{ marginBottom: 16 }}>{item.name}</h2>

			<Descriptions
				column={3}
				size="small"
				bordered
				style={{ marginBottom: 24, background: "#fff", borderRadius: 8 }}
			>
				<Descriptions.Item label="SKU">{item.sku ?? "—"}</Descriptions.Item>
				<Descriptions.Item label="Unit">{item.unit}</Descriptions.Item>
				<Descriptions.Item label="Unit cost">
					{item.unitCost == null ? "—" : `$${Number(item.unitCost).toFixed(2)}`}
				</Descriptions.Item>
				<Descriptions.Item label="Category">
					{item.category ?? "—"}
				</Descriptions.Item>
				<Descriptions.Item label="Preferred vendor">
					{item.preferredVendorName ?? "—"}
				</Descriptions.Item>
				<Descriptions.Item label="Reorder threshold (default)">
					{item.defaultReorderThreshold ?? "—"}
				</Descriptions.Item>
				<Descriptions.Item label="Reorder qty (default)">
					{item.defaultReorderQuantity ?? "—"}
				</Descriptions.Item>
				<Descriptions.Item label="Total on hand" span={2}>
					<Space>
						<span style={{ fontWeight: 600 }}>
							{totalOnHand} {item.unit}
						</span>
						<span style={{ color: "#888" }}>
							across {stock.length} location(s)
						</span>
						{lowCount > 0 && (
							<Tag color="orange" icon={<WarningOutlined />}>
								{lowCount} low
							</Tag>
						)}
					</Space>
				</Descriptions.Item>
				{item.description && (
					<Descriptions.Item label="Description" span={3}>
						{item.description}
					</Descriptions.Item>
				)}
			</Descriptions>

			<h3 style={{ marginTop: 0 }}>
				<BoxPlotOutlined /> Stock by location
			</h3>
			<Table
				columns={stockColumns}
				dataSource={stock}
				rowKey="id"
				pagination={false}
				size="middle"
				rowClassName={(r) => (r.isLowStock ? "inv-row-low" : "")}
				style={{ marginBottom: 24, background: "#fff", borderRadius: 8 }}
			/>

			<h3>
				<HistoryOutlined /> Recent activity
			</h3>
			<Table
				columns={txColumns}
				dataSource={transactions}
				rowKey="id"
				pagination={{ pageSize: 25 }}
				size="middle"
				style={{ background: "#fff", borderRadius: 8 }}
				locale={{
					emptyText: (
						<Empty
							image={<BellOutlined style={{ fontSize: 32, color: "#ccc" }} />}
							description="No transactions yet for this item"
						/>
					),
				}}
			/>
		</div>
	);
};

export { InventoryItemDetail };
